/**
 * Service Request Manager
 * Generic class for collecting product service requests (samples, viewing service, etc.)
 * Accepts a config object to support multiple service types.
 */
if (typeof window.ServiceRequestManager === 'undefined') {
  class ServiceRequestManager {
    constructor(config = {}) {
      this.serviceType = config.serviceType || 'sampleRequest';
      this.STORAGE_KEY = config.storageKey || 'vkw_sample_request';
      this.dataPrefix = config.dataPrefix || 'sample-request';
      this.MAX_ITEMS = config.maxItems || 7;
      this.pageUrl = config.pageUrl || '/pages/staal-aanvragen';
      this.strings = config.strings || {};
      this.items = [];
      this.isInitialized = false;

      this.init();
    }

    init() {
      this.loadItems();
      this.bindEvents();
      this.updateUI();
      this.checkSharedList();
      this.checkFormSuccess();

      this.isInitialized = true;
    }

    checkFormSuccess() {
      const successElement = document.querySelector(`.${this.dataPrefix}-success`);
      if (successElement && this.items.length > 0) {
        this.items = [];
        this.saveItems();
      }
    }

    // LocalStorage Management
    getFromStorage(key) {
      try {
        return localStorage.getItem(key);
      } catch (e) {
        console.error(`${this.serviceType}: Failed to read from localStorage`, e);
        return null;
      }
    }

    setToStorage(key, value) {
      try {
        localStorage.setItem(key, value);
        return true;
      } catch (e) {
        console.error(`${this.serviceType}: Failed to write to localStorage`, e);
        return false;
      }
    }

    // Data Management
    loadItems() {
      const storageData = this.getFromStorage(this.STORAGE_KEY);
      if (storageData) {
        try {
          this.items = JSON.parse(storageData);
        } catch (e) {
          this.items = [];
        }
      }
    }

    saveItems() {
      const data = JSON.stringify(this.items);
      this.setToStorage(this.STORAGE_KEY, data);
      this.updateUI();

      // Update combined dropdown counts if it exists
      if (typeof updateServicesDropdown === 'function') {
        updateServicesDropdown();
      }
    }

    addItem(productData) {
      if (this.items.length >= this.MAX_ITEMS) {
        this.showNotification(this.strings.maxReached || `Maximum ${this.MAX_ITEMS} items bereikt`);
        return false;
      }

      const existingIndex = this.items.findIndex(item =>
        item.productId === productData.productId
      );

      if (existingIndex === -1) {
        this.items.push({
          ...productData,
          addedAt: new Date().toISOString()
        });
        this.saveItems();
        this.showNotification(this.strings.addedSuccess || 'Product toegevoegd');

        document.dispatchEvent(new CustomEvent(`${this.serviceType}:added`, {
          detail: { productId: productData.productId, productData }
        }));

        return true;
      } else {
        this.showNotification(this.strings.alreadyAdded || 'Product al toegevoegd');
      }
      return false;
    }

    removeItem(productId) {
      this.items = this.items.filter(item => item.productId !== productId);
      this.saveItems();
      this.showNotification(this.strings.removedSuccess || 'Product verwijderd');

      document.dispatchEvent(new CustomEvent(`${this.serviceType}:removed`, {
        detail: { productId }
      }));
    }

    clearItems() {
      if (confirm(this.strings.clearConfirm || 'Weet u zeker dat u de lijst wilt leegmaken?')) {
        this.items = [];
        this.saveItems();
        this.showNotification(this.strings.clearedSuccess || 'Lijst geleegd');
      }
    }

    // UI Updates
    updateUI() {
      this.updateCount();
      this.updateToggleButtons();
      this.renderListPage();
      this.updateFormFields();
    }

    updateCount() {
      const countElements = document.querySelectorAll(`[data-${this.dataPrefix}-count]`);
      countElements.forEach(el => {
        el.textContent = this.items.length;
        el.classList.toggle(`${this.dataPrefix}-nav__count--empty`, this.items.length === 0);
      });

      const currentEl = document.querySelector(`[data-${this.dataPrefix}-current]`);
      if (currentEl) {
        currentEl.textContent = this.items.length;
      }
    }

    updateToggleButtons() {
      const buttons = document.querySelectorAll(`[data-${this.dataPrefix}-toggle]`);

      buttons.forEach(button => {
        const productId = button.getAttribute('data-product-id');
        const isInList = this.items.some(item => item.productId === productId);
        button.classList.toggle('is-active', isInList);
        button.setAttribute('aria-pressed', isInList);

        const textEl = button.querySelector(`.${this.dataPrefix}-toggle__text`);
        if (textEl) {
          const addText = button.getAttribute('data-text-add') || 'Request';
          const addedText = button.getAttribute('data-text-added') || 'Added';
          textEl.textContent = isInList ? addedText : addText;
        }
      });
    }

    updateFormFields() {
      const productIdsField = document.querySelector(`[data-${this.dataPrefix}-product-ids]`);
      const productHandlesField = document.querySelector(`[data-${this.dataPrefix}-product-handles]`);
      const productTitlesField = document.querySelector(`[data-${this.dataPrefix}-product-titles]`);
      const listUrlField = document.querySelector(`[data-${this.dataPrefix}-list-url]`);

      if (productIdsField) {
        productIdsField.value = this.items.map(item => item.productId).join(', ');
      }
      if (productHandlesField) {
        productHandlesField.value = this.items.map(item => item.handle).join(', ');
      }
      if (productTitlesField) {
        productTitlesField.value = this.items.map(item => item.title).join(', ');
      }
      if (listUrlField && this.items.length > 0) {
        listUrlField.value = this.generateShareUrl();
      }
    }

    renderListPage() {
      const container = document.querySelector(`[data-${this.dataPrefix}-items]`);
      const emptyState = document.querySelector(`[data-${this.dataPrefix}-empty]`);
      const formSection = document.querySelector(`[data-${this.dataPrefix}-form-section]`);
      const formId = this.dataPrefix === 'sample-request' ? 'SampleRequestForm' : 'ViewingServiceForm';
      const submitButton = document.querySelector(`#${formId} button[type="submit"]`);

      if (!container) return;

      if (this.items.length === 0) {
        container.style.display = 'none';
        if (emptyState) emptyState.style.display = 'block';
        if (formSection) formSection.style.display = 'none';
        if (submitButton) submitButton.disabled = true;
        return;
      }

      container.style.display = 'block';
      if (emptyState) emptyState.style.display = 'none';
      if (formSection) formSection.style.display = 'block';
      if (submitButton) submitButton.disabled = false;

      container.innerHTML = this.items.map((item, index) => `
        <div class="${this.dataPrefix}-item" data-${this.dataPrefix}-item="${item.productId}">
          <span class="${this.dataPrefix}-item__number">${index + 1}</span>
          <div class="${this.dataPrefix}-item__image">
            <a href="${item.url}">
              <img src="${item.image}" alt="${item.title}" loading="lazy">
            </a>
          </div>
          <div class="${this.dataPrefix}-item__details">
            <h3 class="${this.dataPrefix}-item__title">
              <a href="${item.url}">${item.title}</a>
            </h3>
          </div>
          <div class="${this.dataPrefix}-item__actions">
            <button type="button" class="${this.dataPrefix}-item__remove" data-${this.dataPrefix}-remove="${item.productId}" title="Verwijderen">
              <svg width="14" height="14" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">
                <g stroke="currentColor" fill="none" stroke-linecap="square">
                  <path d="M2 2l10 10M12 2L2 12"></path>
                </g>
              </svg>
            </button>
          </div>
        </div>
      `).join('');
    }

    // Render compact item list for header dropdown
    renderDropdownItems(container) {
      if (!container) return;

      if (this.items.length === 0) {
        container.innerHTML = '<p class="services-dropdown__empty">Geen producten toegevoegd</p>';
        return;
      }

      container.innerHTML = this.items.map(item => `
        <div class="services-dropdown__item">
          <div class="services-dropdown__item-image">
            <img src="${item.image}" alt="${item.title}" loading="lazy">
          </div>
          <div class="services-dropdown__item-title">${item.title}</div>
          <button class="services-dropdown__item-remove" data-${this.dataPrefix}-remove="${item.productId}" title="Verwijderen">
            <svg width="10" height="10" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">
              <g stroke="currentColor" fill="none" stroke-linecap="square">
                <path d="M2 2l10 10M12 2L2 12"></path>
              </g>
            </svg>
          </button>
        </div>
      `).join('');
    }

    // Event Handlers
    bindEvents() {
      document.addEventListener('click', (e) => {
        const toggleBtn = e.target.closest(`[data-${this.dataPrefix}-toggle]`);
        if (toggleBtn) {
          e.preventDefault();
          this.handleToggle(toggleBtn);
        }

        const removeBtn = e.target.closest(`[data-${this.dataPrefix}-remove]`);
        if (removeBtn) {
          e.preventDefault();
          const productId = removeBtn.getAttribute(`data-${this.dataPrefix}-remove`);
          this.removeItem(productId);
        }

        const clearBtn = e.target.closest(`[data-${this.dataPrefix}-clear]`);
        if (clearBtn) {
          e.preventDefault();
          this.clearItems();
        }

        const shareBtn = e.target.closest(`[data-${this.dataPrefix}-share]`);
        if (shareBtn) {
          e.preventDefault();
          this.copyShareLink();
        }
      });

      const form = document.querySelector(`[data-${this.dataPrefix}-form]`);
      if (form) {
        form.addEventListener('submit', (e) => {
          this.updateFormFields();
        });
      }
    }

    handleToggle(button) {
      const productId = button.getAttribute('data-product-id');
      const isInList = this.items.some(item => item.productId === productId);

      if (isInList) {
        this.removeItem(productId);
      } else {
        const productData = {
          productId: productId,
          title: button.getAttribute('data-product-title'),
          image: button.getAttribute('data-image'),
          url: button.getAttribute('data-url'),
          handle: button.getAttribute('data-handle')
        };

        this.addItem(productData);
      }
    }

    // Share Functionality
    generateShareUrl() {
      const shareData = this.items.map(item => ({
        p: item.productId,
        h: item.handle
      }));

      const encoded = btoa(JSON.stringify(shareData))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');

      return `${window.location.origin}${this.pageUrl}?share=${encoded}`;
    }

    copyShareLink() {
      if (this.items.length === 0) {
        this.showNotification('Voeg eerst producten toe');
        return;
      }

      const shareUrl = this.generateShareUrl();

      navigator.clipboard.writeText(shareUrl).then(() => {
        this.showNotification(this.strings.linkCopied || 'Link gekopieerd!');
      }).catch(() => {
        const tempInput = document.createElement('input');
        tempInput.value = shareUrl;
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand('copy');
        document.body.removeChild(tempInput);
        this.showNotification(this.strings.linkCopied || 'Link gekopieerd!');
      });
    }

    checkSharedList() {
      const urlParams = new URLSearchParams(window.location.search);
      const shareData = urlParams.get('share');

      if (shareData) {
        try {
          const paddedData = shareData
            .replace(/-/g, '+')
            .replace(/_/g, '/')
            .padEnd(shareData.length + (4 - shareData.length % 4) % 4, '=');

          const parsedData = JSON.parse(atob(paddedData));

          if (Array.isArray(parsedData) && parsedData.length > 0) {
            this.loadSharedList(parsedData);
          }
        } catch (e) {
          console.error(`${this.serviceType}: Invalid share data`, e);
        }
      }
    }

    async loadSharedList(shareItems) {
      this.showNotification('Gedeelde lijst wordt geladen...');

      try {
        const products = [];

        for (const item of shareItems) {
          if (item.h) {
            try {
              const response = await fetch(`/products/${item.h}.js`);
              if (response.ok) {
                const product = await response.json();

                products.push({
                  productId: product.id.toString(),
                  title: product.title,
                  image: product.featured_image || product.images[0] || '',
                  url: `/products/${product.handle}`,
                  handle: product.handle
                });
              }
            } catch (err) {
              console.error('Failed to fetch product', item.h);
            }
          }
        }

        if (products.length > 0) {
          this.items = products.slice(0, this.MAX_ITEMS);
          this.saveItems();
          this.showNotification(`${products.length} producten geladen`);

          window.history.replaceState({}, document.title, window.location.pathname);
        }
      } catch (error) {
        console.error(`${this.serviceType}: Error loading shared list`, error);
      }
    }

    // Notifications
    showNotification(message) {
      const existing = document.querySelector('.service-request-notification');
      if (existing) existing.remove();

      const notification = document.createElement('div');
      notification.className = 'service-request-notification';
      notification.textContent = message;
      document.body.appendChild(notification);

      setTimeout(() => notification.classList.add('is-visible'), 10);

      setTimeout(() => {
        notification.classList.remove('is-visible');
        setTimeout(() => notification.remove(), 300);
      }, 3000);
    }
  }

  // ==========================================
  // Services Dropdown (header combined panel)
  // ==========================================
  function initServicesDropdown() {
    const trigger = document.querySelector('[data-services-dropdown-trigger]');
    const panel = document.querySelector('[data-services-dropdown-panel]');
    if (!trigger || !panel) return;

    // Toggle panel
    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const isOpen = panel.classList.contains('is-open');
      if (isOpen) {
        panel.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
      } else {
        panel.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
        updateServicesDropdown();
      }
    });

    // Tab switching
    panel.addEventListener('click', (e) => {
      const tab = e.target.closest('[data-services-tab]');
      if (tab) {
        const tabName = tab.getAttribute('data-services-tab');
        const allTabs = panel.querySelectorAll('[data-services-tab]');
        const allContents = panel.querySelectorAll('[data-services-tab-content]');

        allTabs.forEach(t => t.classList.remove('is-active'));
        allContents.forEach(c => c.classList.remove('is-active'));

        tab.classList.add('is-active');
        const content = panel.querySelector(`[data-services-tab-content="${tabName}"]`);
        if (content) content.classList.add('is-active');
      }
    });

    // Close on outside click
    document.addEventListener('click', (e) => {
      if (!trigger.contains(e.target) && !panel.contains(e.target)) {
        panel.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
      }
    });

    // Close on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        panel.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
      }
    });
  }

  function updateServicesDropdown() {
    // Update sample request tab
    const sampleContent = document.querySelector('[data-services-tab-content="stalen"]');
    const sampleTab = document.querySelector('[data-services-tab="stalen"]');
    if (sampleContent && window.sampleRequest) {
      window.sampleRequest.renderDropdownItems(sampleContent.querySelector('.services-dropdown__items'));
      if (sampleTab) {
        const countEl = sampleTab.querySelector('.services-dropdown__tab-count');
        if (countEl) countEl.textContent = window.sampleRequest.items.length;
      }
    }

    // Update viewing service tab
    const viewingContent = document.querySelector('[data-services-tab-content="zichtservice"]');
    const viewingTab = document.querySelector('[data-services-tab="zichtservice"]');
    if (viewingContent && window.viewingService) {
      window.viewingService.renderDropdownItems(viewingContent.querySelector('.services-dropdown__items'));
      if (viewingTab) {
        const countEl = viewingTab.querySelector('.services-dropdown__tab-count');
        if (countEl) countEl.textContent = window.viewingService.items.length;
      }
    }

    // Update combined count badge
    const combinedCount = document.querySelector('[data-services-combined-count]');
    if (combinedCount) {
      const sampleCount = window.sampleRequest ? window.sampleRequest.items.length : 0;
      const viewingCount = window.viewingService ? window.viewingService.items.length : 0;
      const total = sampleCount + viewingCount;
      combinedCount.textContent = total;
      combinedCount.classList.toggle('services-dropdown__count--empty', total === 0);
    }
  }

  // Make updateServicesDropdown available globally
  window.updateServicesDropdown = updateServicesDropdown;

  // ==========================================
  // Initialize instances based on settings
  // ==========================================
  function initServiceManagers() {
    // Sample Request
    if (window.sampleRequestSettings?.enabled && !window.sampleRequest) {
      window.sampleRequest = new ServiceRequestManager({
        serviceType: 'sampleRequest',
        storageKey: 'vkw_sample_request',
        dataPrefix: 'sample-request',
        maxItems: window.sampleRequestSettings.maxItems || 7,
        pageUrl: window.sampleRequestSettings.pageUrl || '/pages/staal-aanvragen',
        strings: window.sampleRequestStrings || {}
      });
    }

    // Viewing Service
    if (window.viewingServiceSettings?.enabled && !window.viewingService) {
      window.viewingService = new ServiceRequestManager({
        serviceType: 'viewingService',
        storageKey: 'vkw_viewing_service',
        dataPrefix: 'viewing-service',
        maxItems: window.viewingServiceSettings.maxItems || 5,
        pageUrl: window.viewingServiceSettings.pageUrl || '/pages/zichtservice-aanvragen',
        strings: window.viewingServiceStrings || {}
      });
    }

    // Init dropdown if both services are present in the header
    initServicesDropdown();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initServiceManagers);
  } else {
    initServiceManagers();
  }

  window.ServiceRequestManager = ServiceRequestManager;
  // Backward compat alias
  window.SampleRequestManager = ServiceRequestManager;
}
